import { useState, useCallback } from "react";
import { useItems } from "../hooks/useItems";
import { bulkAction, updateItemStatus } from "../api/items";

export function QuarantineView() {
  const { data, loading, error, reload } = useItems({
    page: 1,
    page_size: 50,
    status: "failed,quarantined,spam",
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!data) return;
    if (selected.size === data.items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.items.map((i) => i.id)));
    }
  };

  const handleBulkDelete = useCallback(async () => {
    if (selected.size === 0) return;
    if (!confirm(`${selected.size} Item(s) endgültig löschen?`)) return;
    await bulkAction({ ids: Array.from(selected), action: "delete" });
    setSelected(new Set());
    reload();
  }, [selected, reload]);

  const handleBulkSpam = useCallback(async () => {
    if (selected.size === 0) return;
    await bulkAction({ ids: Array.from(selected), action: "set_status", status: "spam" });
    setSelected(new Set());
    reload();
  }, [selected, reload]);

  const handleBulkRestore = useCallback(async () => {
    if (selected.size === 0) return;
    await bulkAction({ ids: Array.from(selected), action: "set_status", status: "ok" });
    setSelected(new Set());
    reload();
  }, [selected, reload]);

  const handleSingleRestore = async (id: number) => {
    await updateItemStatus(id, "ok");
    reload();
  };

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  };

  const statusLabel = (s: string) => {
    switch (s) {
      case "quarantined": return "Quarantäne";
      case "failed": return "Fehler";
      case "spam": return "Spam";
      default: return s;
    }
  };

  return (
    <div>
      <h2 className="admin-page-title">Quarantäne & Spam-Verwaltung</h2>
      <p className="text-muted admin-page-desc">
        Items mit Fehlern, in Quarantäne oder als Spam markiert. Spam wird nach 30 Tagen automatisch gelöscht.
      </p>

      {error && <p className="error-text">Fehler: {error}</p>}

      {selected.size > 0 && (
        <div className="bulk-actions">
          <span className="selected-count">{selected.size} ausgewählt</span>
          <button className="btn-sm btn-primary" onClick={handleBulkRestore}>In Feed verschieben</button>
          <button className="btn-sm btn-ghost" onClick={handleBulkSpam}>Als Spam</button>
          <button className="btn-sm btn-danger" onClick={handleBulkDelete}>Endgültig löschen</button>
        </div>
      )}

      {loading && !data && <p className="loading-text">Lade...</p>}

      {data && data.items.length === 0 && (
        <div className="empty-state">
          <p>Keine Einträge in der Quarantäne.</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <table className="item-table">
          <thead>
            <tr>
              <th className="cell-checkbox">
                <input
                  type="checkbox"
                  checked={selected.size === data.items.length}
                  onChange={toggleAll}
                />
              </th>
              <th>Datum</th>
              <th>Betreff</th>
              <th>Status</th>
              <th>Score</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.id} className={selected.has(item.id) ? "selected" : ""}>
                <td className="cell-checkbox">
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                  />
                </td>
                <td className="cell-date">{formatDate(item.published_at)}</td>
                <td className="cell-title">{item.title || "Ohne Titel"}</td>
                <td>
                  <span className={`status-badge status-${item.status}`}>
                    {statusLabel(item.status)}
                  </span>
                </td>
                <td>{item.relevance_score !== null ? Math.round(item.relevance_score) : "—"}</td>
                <td>
                  <button className="btn-sm btn-ghost" onClick={() => handleSingleRestore(item.id)}>
                    Wiederherstellen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
